import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import SectionMessage from '@atlaskit/section-message';
import AddonDiscovery, {
  AddonDiscoveryUtils,
  FeaturedAddonsConfig,
  getFilters,
} from '@atlassian/amkt-addon-discovery';
import { Link } from '@atlassian/amkt-common';
import { withFeatureFlags, withStatsig } from '@atlassian/mpac-feature-flags';
import { Meta } from '@atlassian/mpac-primitives';
import { Card } from '@atlassian/mpac-ui';
import { encodeAppName, LinkUtils, UrlUtils } from '@atlassian/mpac-utils';
import styled from '@emotion/styled';
import classNames from 'classnames';
import { fromJS, List, Map, Set } from 'immutable';
import React, { Component } from 'react';
import { ScreenClassRender } from 'react-grid-system';
import ScrollToTop from 'react-scroll-up';

import RotatableEntity from '../../../../../../../../common/records/RotatableEntity';
import resolveContentfulClient from '../../../../../../../../common/utils/contentful-client';
import { HomePageTask, SearchPageTask } from '../../../../../../../telemetry/tasks';
import SearchConfigValidation from '../../../../../../common/SearchConfigValidation';
import {
  mpacHosting,
  navigateToAddon,
  resolveHitHref,
  resolveHitImageHref,
  resolveHostingFromHit,
} from '../../../../../utils/AddonDiscovery';
import * as perspectives from '../../../constants/perspectives';
import getFeaturedCategories from '../config/featuredCategories';
import { resolveMetricsKey, resolvePathAndQuery } from '../utils/links';
import CategoryTilesPanel from './CategoryTilesPanel';
import EmailHit from './EmailHit';
import Hero from './Hero';

import type CommonPropTypes from '../../../../../../../constants/PropTypes';
import type { PropsWithFeatureFlags, StatsigClientType } from '@atlassian/mpac-feature-flags';
import type ImmutablePropTypes from '@atlassian/react-immutable-proptypes';
import type PropTypes from 'prop-types';

import styles from './AddonDiscoveryWrapper.less';

export const TopPanelContainer = styled.div`
  width: 100%;
`;
export const EmailHitCard = styled(Card)`
  padding: 0;
  height: 100%;
`;

const scrollToTopParams = {
  showUnder: 500,
  easing: 'easeOutQuad',
  style: {
    position: 'fixed',
    bottom: 39,
    right: 30,
    cursor: 'pointer',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'linear',
    transitionDelay: '0s',
  },
} as const;

type AddonDiscoveryWrapperProps = {
  generalLinks: typeof CommonPropTypes.generalLinks;
  config: typeof CommonPropTypes.addonDiscoveryConfig;
  commonConfig: typeof CommonPropTypes.commonConfig;
  filters: typeof ImmutablePropTypes.map;
  history: typeof CommonPropTypes.history;
  onCategoryTileClick: typeof PropTypes.func;
  onLoadingComplete: typeof PropTypes.func;
  onLoadingStart: typeof PropTypes.func;
  onSubscribeToMailingListClicked?: typeof PropTypes.func;
  perspective?: typeof perspectives.propType;
  solutionsConfig: typeof CommonPropTypes.solutionsConfig;
  enableJsdToJsm: typeof PropTypes.bool;
  showAiAppsCollectionTile: typeof PropTypes.bool;
  showChangemakersCollectionTileFlag: typeof PropTypes.bool;
  statsigClient: StatsigClientType;
};

class AddonDiscoveryWrapper extends Component<PropsWithFeatureFlags<AddonDiscoveryWrapperProps>> {
  constructor(props: any) {
    super(props);

    // @ts-expect-error [MC-2850] - TS2339 - Property 'hitContext' does not exist on type 'AddonDiscoveryWrapper'.
    this.hitContext = Map({
      hitHrefResolver: this.hitHrefResolver,
      hitImageHrefResolver: this.hitImageHrefResolver,
      onHitClick: this.onHitClick,
      onRatingsClick: this.onRatingsClick,
    });

    // @ts-expect-error [MC-2850] - TS2339 - Property 'filtersContext' does not exist on type 'AddonDiscoveryWrapper'.
    this.filtersContext = Map({
      discoverFacetHrefResolver: this.discoverFacetHrefResolver,
      searchFacetHrefResolver: this.searchFacetHrefResolver,
    });

    // @ts-expect-error [MC-2850] - TS2339 - Property 'transitions' does not exist on type 'AddonDiscoveryWrapper'.
    this.transitions = Map({
      transitionCompleted: this.transitionCompleted,
      transitionStarted: this.transitionStarted,
    });
  }

  state = {
    featuredAddonsConfig: undefined,
  };

  startTelemetryTaskTracker() {
    // @ts-expect-error [MC-2850] - TS2339 - Property 'perspective' does not exist on type 'Readonly<{}> & Readonly<{ children?: ReactNode; }>'.
    if (this.props.perspective === perspectives.DISCOVER) {
      HomePageTask.start();
    } else {
      SearchPageTask.start();
    }
  }

  setFeaturedCollections() {
    const commonConfig = this.props.commonConfig;
    resolveContentfulClient({ commonConfig })
      .getEntries({
        'metadata.tags.sys.id[in]': [
          'marketplaceRisingStar',
          'marketplaceBestSeller',
          'marketplaceSpotlight',
        ],
      })
      .then((results) => {
        const featuredAddonsConfig: Record<string, any> = {};
        results.items.forEach((result) => {
          const entry = result.fields as {
            key: string;
            title: string;
            description: string;
            curatedApps: { fields: { pinned: string[]; unpinned: string[] } };
          };

          if (!featuredAddonsConfig[entry.key]) {
            featuredAddonsConfig[entry.key] = [];
          }
          featuredAddonsConfig[entry.key].push({
            key: result.metadata.tags[0].sys.id,
            title: entry.title,
            tagline: entry.description,
            curatedApps: new RotatableEntity({
              pinned: List(entry.curatedApps.fields.pinned),
              unpinned: List(entry.curatedApps.fields.unpinned),
            }).resolveItems(9),
          });
        });
        return featuredAddonsConfig;
      })
      .then((featuredAddonsConfig) => {
        this.setState({ featuredAddonsConfig: new FeaturedAddonsConfig(featuredAddonsConfig) });
      })
      .catch((error) => {
        this.setState({ featuredAddonsConfig: new FeaturedAddonsConfig() });
        console.error(
          'Contentful error while fetching custom criteria collections (spotlight, bestSeller and risingStar):',
          error
        );
      });
  }

  componentDidMount() {
    this.startTelemetryTaskTracker();
    this.setFeaturedCollections();
  }

  render() {
    const { enableJsdToJsm } = this.props;
    const meta = AddonDiscoveryUtils.resolveMeta({
      filters: this.props.filters,
      perspectiveKey: this.props.perspective,
      featureFlags: {
        enableJsdToJsm,
      },
    });
    return (
      // @ts-expect-error [MC-2850] - TS2339 - Property 'commonConfig' does not exist on type 'Readonly<{}> & Readonly<{ children?: ReactNode; }>'.
      <SearchConfigValidation config={this.props.commonConfig.algoliaConfig}>
        {() => (
          <div className={classNames(styles.addonDiscoveryWrapper, 'ui-addon-discovery-wrapper')}>
            <Meta description={meta.get('description')} title={meta.get('title')} />
            {this.state.featuredAddonsConfig && (
              <ScreenClassRender render={this.renderAddonDiscovery}>
                <span />
              </ScreenClassRender>
            )}
            {/* @ts-expect-error [MC-2850] - TS2339 - Property 'perspective' does not exist on type 'Readonly<{}> & Readonly<{ children?: ReactNode; }>'. */}
            {this.props.perspective === perspectives.SEARCH ? (
              <ScrollToTop
                showUnder={scrollToTopParams.showUnder}
                easing={scrollToTopParams.easing}
                style={scrollToTopParams.style}
              >
                <span className={styles.scrollToTop}>&#8593; Back to top</span>
              </ScrollToTop>
            ) : undefined}
          </div>
        )}
      </SearchConfigValidation>
    );
  }

  shouldRenderStrideHipchatBanner = () => {
    return (
      // @ts-expect-error [MC-2850] - TS2339 - Property 'commonConfig' does not exist on type 'Readonly<{}> & Readonly<{ children?: ReactNode; }>'.
      this.props.commonConfig.features.strideHipchatBanners &&
      ['stride'].includes(this.props.filters.get('product'))
    );
  };

  shouldRenderCategoryTileSet = (categoryTileSet) => {
    // @ts-ignore MC-2850
    return categoryTileSet && this.props.perspective === perspectives.DISCOVER;
  };

  renderTopPanel = (sc) => {
    const categoryTileSet = getFeaturedCategories({
      jsdToJsmEnabled: this.props.enableJsdToJsm,
      showAiAppsCollectionTile: this.props.showAiAppsCollectionTile,
      showChangemakersCollectionTileFlag: this.props.showChangemakersCollectionTileFlag,
    }).get(this.props.filters.get('product') || 'home');

    return this.shouldRenderStrideHipchatBanner() ||
      this.shouldRenderCategoryTileSet(categoryTileSet) ? (
      <TopPanelContainer>
        {this.shouldRenderStrideHipchatBanner() ? (
          // @ts-expect-error [MC-2850] - TS2322 - Type '{ children: Element; apperance: string; }' is not assignable to type 'IntrinsicAttributes & SectionMessageProps & RefAttributes<HTMLElement>'.
          <SectionMessage apperance="info">
            <div>
              Atlassian recently announced a strategic partnership with Slack that will impact
              Hipchat and Stride apps.&nbsp;
              <Link
                href="https://www.atlassian.com/partnerships/slack/faq#ecosystem"
                rel="noopener noreferrer"
                target="_blank"
              >
                Learn more
              </Link>
            </div>
          </SectionMessage>
        ) : null}
        {this.shouldRenderCategoryTileSet(categoryTileSet) ? (
          <CategoryTilesPanel
            // @ts-expect-error [MC-2850]
            categoryTileSet={categoryTileSet}
            // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
            onCategoryTileClick={this.props.onCategoryTileClick}
            screenClass={sc}
            // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
            solutionsConfig={this.props.solutionsConfig}
          />
        ) : null}
      </TopPanelContainer>
    ) : null;
  };

  renderAddonDiscovery = (sc) => {
    const hideTextSearch = Set(['xl', 'lg']).contains(sc);

    return (
      <AddonDiscovery
        // to convert it to a Map, which amkt-addon-discovery expects
        // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
        algoliaConfig={fromJS(this.props.commonConfig.algoliaConfig.toJS())}
        containerClassName={styles.addonDiscovery}
        debug={process.env.NODE_ENV !== 'production'}
        // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
        environment={this.props.commonConfig.environment}
        featuredAddonsConfig={this.state.featuredAddonsConfig}
        filters={this.props.filters}
        // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
        filtersContext={this.filtersContext}
        heroComponent={Hero}
        hideTextSearch={hideTextSearch}
        // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
        hitContext={this.hitContext}
        perspective={this.props.perspective}
        screenClass={sc}
        stateChangeRequested={this.stateChangeRequested}
        topPanel={this.renderTopPanel(sc)}
        // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
        transitions={this.transitions}
        discoverHeading={
          // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'. | TS2532 - Object is possibly 'undefined'.
          this.props.perspective === perspectives.DISCOVER ? this.renderBreadcrumbs() : null
        }
      />
    );
  };

  onClickBreadcrumbsItem = (e, { filters }) => {
    const { enableJsdToJsm } = this.props;
    const pathAndQuery = resolvePathAndQuery({
      filters,
      // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
      links: this.props.config.links,
      toPerspective: perspectives.DISCOVER,
      featureFlags: {
        enableJsdToJsm,
      },
    });
    LinkUtils.maybeOpenInNewTab(e, () => {
      // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
      this.props.history.push({
        pathname: pathAndQuery.get('path'),
        search: UrlUtils.resolveSearchFromImmutableQuery(pathAndQuery.get('query')),
      });
    });
  };

  renderBreadcrumbs = () => {
    const { enableJsdToJsm } = this.props;
    const product = this.props.filters.get('product');
    return (
      <div
        className={classNames({
          [styles.discoverBreadcrumbs]: true,
          [styles.discoverBreadcrumbsHidden]: !product,
        })}
      >
        <Breadcrumbs>
          <BreadcrumbsItem
            // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
            href={this.props.config.links.home.href}
            text="Marketplace"
            onClick={(e) => this.onClickBreadcrumbsItem(e, { filters: Map() })}
          />
          {product ? (
            <BreadcrumbsItem
              href={this.resolveURLForPerspectiveAndFilters({
                perspective: this.props.perspective,
                filters: this.props.filters,
              })}
              text={getFilters({
                featureFlags: {
                  enableJsdToJsm,
                },
              }).getIn(['product', 'customFilters', product, 'label'])}
              onClick={(e) => this.onClickBreadcrumbsItem(e, { filters: this.props.filters })}
            />
          ) : null}
        </Breadcrumbs>
      </div>
    );
  };

  renderEmailHit = ({ screenClass }) => {
    return (
      <EmailHitCard>
        <EmailHit
          screenClass={screenClass}
          // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
          onButtonClick={this.props.onSubscribeToMailingListClicked}
        />
      </EmailHitCard>
    );
  };

  getMetricsKey() {
    const { enableJsdToJsm } = this.props;
    return resolveMetricsKey({
      perspective: this.props.perspective,
      filters: this.props.filters,
      // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
      metrics: this.props.config.metricsKeys,
      featureFlags: {
        enableJsdToJsm,
      },
    });
  }

  transitionCompleted = () => {
    // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'. | TS2532 - Object is possibly 'undefined'.
    this.props.onLoadingComplete({ metricsKey: this.getMetricsKey() });
  };

  transitionStarted = () => {
    // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'. | TS2532 - Object is possibly 'undefined'.
    this.props.onLoadingStart({ metricsKey: this.getMetricsKey() });
    this.startTelemetryTaskTracker();
  };

  onHitClick = ({ hit }) => {
    navigateToAddon(
      {
        appId: hit.get('addonId'),
        appName: hit.get('name'),
        hosting: resolveHostingFromHit({ hit, filters: this.props.filters }),
        generalLinks: this.props.generalLinks,
      },
      this.props.history
    );
  };

  hitHrefResolver = ({ hit }) => {
    return resolveHitHref({
      appId: hit.get('addonId'),
      appName: hit.get('name'),
      hosting: resolveHostingFromHit({ hit, filters: this.props.filters }),
      generalLinks: this.props.generalLinks,
    });
  };

  stateChangeRequested = ({ filters, perspective }) => {
    const { enableJsdToJsm } = this.props;
    const newFilters = filters || this.props.filters;
    const pathAndQuery = resolvePathAndQuery({
      filters: newFilters,
      // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
      links: this.props.config.links,
      toPerspective: perspective || this.props.perspective,
      featureFlags: {
        enableJsdToJsm,
      },
    });
    // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
    this.props.history.push({
      pathname: pathAndQuery.get('path'),
      search: UrlUtils.resolveSearchFromImmutableQuery(pathAndQuery.get('query')),
    });
  };

  discoverFacetHrefResolver = ({ filters }) =>
    this.resolveURLForPerspectiveAndFilters({ perspective: perspectives.DISCOVER, filters });

  searchFacetHrefResolver = ({ filters }) =>
    this.resolveURLForPerspectiveAndFilters({ perspective: perspectives.SEARCH, filters });

  resolveURLForPerspectiveAndFilters({ perspective, filters }) {
    const { enableJsdToJsm } = this.props;
    const pathAndQuery = resolvePathAndQuery({
      filters,
      // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
      links: this.props.config.links,
      toPerspective: perspective,
      featureFlags: {
        enableJsdToJsm,
      },
    });

    return `${pathAndQuery.get('path')}${UrlUtils.resolveSearchFromImmutableQuery(
      pathAndQuery.get('query')
    )}`;
  }

  onRatingsClick = ({ hit }) => {
    const appListingUrl = UrlUtils.resolveUrlFromTemplateAndParameters({
      // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
      template: this.props.generalLinks.addonListingWithHosting.href,
      parameters: {
        pluginId: hit.get('addonId'),
        appName: encodeAppName(hit.get('name')),
        hosting: mpacHosting(resolveHostingFromHit({ hit, filters: this.props.filters })),
        tab: 'reviews',
      },
    });

    // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
    this.props.history.push(appListingUrl);
  };

  hitImageHrefResolver = ({ hit }) => {
    return resolveHitImageHref({ hit, generalLinks: this.props.generalLinks });
  };
}

export default withFeatureFlags<AddonDiscoveryWrapperProps>(withStatsig(AddonDiscoveryWrapper));
